import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Text,
} from "@chakra-ui/react";
import { useStytch, useStytchUser } from "@stytch/react";
import { PasswordAuthenticateOptions } from "@stytch/vanilla-js";
import { useMutation } from "@tanstack/react-query";
import { AuthHelpButton } from "features/auth/AuthHelpButton";
import { useAuthCancel, useAuthContinue } from "features/auth/AuthUtils";
import {
  getStytchSessionDurationMinutes,
  STYTCH_PW_RESET_URL,
  STYTCH_REDIRECT_URL,
  useStytchFormErrorHandler,
} from "features/auth/StytchUtils";
import React from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { CardCloseButton } from "shared/components/card/CardCloseButton";
import Form from "shared/components/form/Form";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { useToast } from "shared/components/toast/useToast";

const useStytchPasswordAuthenticate = () => {
  const stytchClient = useStytch();
  return useMutation({
    mutationFn: async (options: PasswordAuthenticateOptions) => {
      await stytchClient.passwords.authenticate(options);
    },
  });
};

export const AuthPasswordPage: React.FC = () => {
  const continueAuth = useAuthContinue();
  const cancelAuth = useAuthCancel();

  const { user: stytchUser } = useStytchUser();

  if (!stytchUser) {
    return <Navigate to="/auth/login" replace={true} />;
  }

  const email = stytchUser.emails[0].email;
  return (
    <AuthPasswordForm
      email={email}
      onSuccess={continueAuth}
      onCancel={cancelAuth}
    />
  );
};

export const AuthPasswordForm: React.FC<
  React.PropsWithChildren<{
    email: string;
    onSuccess: () => void;
    onCancel: () => void;
  }>
> = ({ email, onSuccess, onCancel, children }) => {
  const { t } = useTranslation(["auth", "common"]);
  const toast = useToast();

  const stytchClient = useStytch();
  const onStytchError = useStytchFormErrorHandler();

  const { mutateAsync: authenticate, isPending: isAuthenticating } =
    useStytchPasswordAuthenticate();

  const form = useForm<PasswordAuthenticateOptions>({
    defaultValues: {
      email,
      session_duration_minutes: getStytchSessionDurationMinutes(),
    },
  });
  return (
    <Form
      form={form}
      disableNavBlocker={true}
      onSubmit={authenticate}
      onSuccess={onSuccess}
      onError={onStytchError}
    >
      <Card width="md" gap={0} pb={0}>
        <CardHeader>
          <Box pb="16px">
            <Text textStyle="h4">{t("auth:login")}</Text>
            <Text pt="24px">
              <Trans
                ns="auth"
                i18nKey="enterPasswordForEmail"
                values={{ email }}
              >
                Enter password for
                <Text as="span" textStyle="bodyBold">
                  {email}
                </Text>
                .
              </Trans>
            </Text>
          </Box>
          <CardCloseButton onClick={onCancel} />
        </CardHeader>
        <CardBody>
          <input type="hidden" autoComplete="username" value={email} />
          <FormTextInput
            name="password"
            type="password"
            autoComplete="current-password"
            title={t("auth:password")}
            autoFocus={true}
          />
        </CardBody>
        <CardFooter pb="24px">
          <AuthHelpButton
            onClick={async () => {
              try {
                await stytchClient.passwords.resetByEmailStart({
                  email,
                  login_redirect_url: STYTCH_REDIRECT_URL,
                  reset_password_redirect_url: STYTCH_PW_RESET_URL,
                  reset_password_expiration_minutes:
                    getStytchSessionDurationMinutes(),
                  login_expiration_minutes: getStytchSessionDurationMinutes(),
                });
                toast.showToast({
                  variant: "success",
                  message: (
                    <Trans ns="auth" i18nKey="emailSentTo" values={{ email }}>
                      Email sent to
                      <Text as="span" fontWeight="semibold">
                        user@example.com
                      </Text>
                    </Trans>
                  ),
                });
              } catch (e) {
                toast.showErrorToast(e);
              }
            }}
          >
            {t("auth:forgotPassword")}
          </AuthHelpButton>
          <Button type="submit" isLoading={isAuthenticating}>
            {t("common:continue")}
          </Button>
        </CardFooter>
        {children}
      </Card>
    </Form>
  );
};
